/**
 * Сервис авторизации для фронтенда
 */

// Если VITE_API_URL не задан, используем прокси (пустая строка = относительный путь)
const API_URL = import.meta.env.VITE_API_URL || '';
const TOKEN_KEY = 'fabrica_auth_token';
const USER_KEY = 'fabrica_user';
const ACCESS_CACHE_KEY = 'fabrica_access_cache';

// В dev режиме храним токен в localStorage (для работы через Vite proxy)
// В production будет HTTP-only cookie

export interface User {
  id: string;
  email: string;
  is_active: number;
  is_admin: number;
  subscription_type: 'free' | 'basic' | 'pro' | 'enterprise';
  subscription_expires_at: string | null;
  created_at: string;
  last_login_at: string | null;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface AccessCheck {
  allowed: boolean;
  reason?: string;
  subscriptionType?: string;
  expiresAt?: string;
  canAccessEditor?: boolean;
  offlineFallback?: boolean;
}

class AuthService {
  private onAuthChangeCallbacks: ((user: User | null) => void)[] = [];

  private getCachedAccess(): AccessCheck | null {
    try {
      const raw = localStorage.getItem(ACCESS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as AccessCheck | null;
      if (!parsed || typeof parsed !== 'object' || typeof parsed.allowed !== 'boolean') {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private storeAccess(access: AccessCheck): void {
    try {
      localStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify(access));
    } catch (e) {
      console.error('🔐 Failed to store access state:', e);
    }
  }

  private getOfflineGraceAccess(user: User | null): AccessCheck {
    const cached = this.getCachedAccess();
    if (cached) {
      return {
        ...cached,
        offlineFallback: true
      };
    }

    return {
      // Не перекрываем работу резко, если сеть пропала во время сессии.
      allowed: true,
      canAccessEditor: true,
      offlineFallback: true,
      subscriptionType: user?.subscription_type,
      expiresAt: user?.subscription_expires_at ?? undefined
    };
  }

  constructor() {
    const isDev = import.meta.env.DEV;
    if (isDev) {
      console.log('🔐 AuthService created (dev mode)');
    }
  }
  
  // Читаем токен из localStorage
  private getStoredToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }
  
  // Сохранить токен в localStorage
  private storeToken(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      console.error('🔐 Failed to store token:', e);
    }
  }
  
  // Удалить токен из localStorage
  private clearToken(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      console.error('🔐 Failed to clear token:', e);
    }
  }
  
  // Читаем пользователя напрямую из localStorage каждый раз
  private getStoredUser(): User | null {
    try {
      const json = localStorage.getItem(USER_KEY);
      if (json) {
        return JSON.parse(json);
      }
    } catch (e) {
      console.error('🔐 Failed to parse stored user:', e);
    }
    return null;
  }
  
  // Сохранить пользователя в localStorage
  private storeUser(user: User): void {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch (e) {
      console.error('🔐 Failed to store user:', e);
    }
  }
  
  // Удалить пользователя из localStorage
  private clearUser(): void {
    try {
      localStorage.removeItem(USER_KEY);
    } catch (e) {
      console.error('🔐 Failed to clear user:', e);
    }
  }

  private clearAccessCache(): void {
    try {
      localStorage.removeItem(ACCESS_CACHE_KEY);
    } catch (e) {
      console.error('🔐 Failed to clear access cache:', e);
    }
  }
  
  // Для совместимости
  reinit(): void {
    console.log('🔐 reinit called');
  }

  // Подписка на изменение состояния авторизации
  onAuthChange(callback: (user: User | null) => void): () => void {
    this.onAuthChangeCallbacks.push(callback);
    return () => {
      this.onAuthChangeCallbacks = this.onAuthChangeCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyAuthChange(): void {
    const user = this.getUser();
    this.onAuthChangeCallbacks.forEach(cb => cb(user));
  }

  // Получить текущего пользователя
  getUser(): User | null {
    return this.getStoredUser();
  }

  // Проверить, авторизован ли пользователь
  // В production токен в httpOnly cookie — проверяем только наличие user в localStorage
  isAuthenticated(): boolean {
    const token = this.getStoredToken();
    const user = this.getStoredUser();
    if (import.meta.env.DEV) {
      console.log('🔐 isAuthenticated check:', { hasToken: !!token, hasUser: !!user, email: user?.email });
    }
    if (import.meta.env.PROD) {
      return !!user; // в production авторизация по cookie, токен в JSON не приходит
    }
    return !!token && !!user;
  }

  // Проверить, является ли пользователь админом
  isAdmin(): boolean {
    return !!this.getStoredUser()?.is_admin;
  }
  
  // Получить токен для запросов
  getToken(): string | null {
    return this.getStoredToken();
  }

  // Авторизация
  async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // ВАЖНО: отправляем и получаем cookies
        body: JSON.stringify({ email, password })
      });

      // Проверяем что ответ не пустой
      const text = await response.text();
      let data;
      
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('🔐 Failed to parse response:', text);
        return { success: false, error: 'Ошибка сервера: неверный формат ответа' };
      }

      if (!response.ok) {
        console.error('🔐 Login failed:', response.status, data);
        return { success: false, error: data.error || 'Ошибка авторизации' };
      }

      // В production сервер отдаёт только user, токен — в httpOnly cookie
      if (!data.user) {
        console.error('🔐 Invalid response data:', data);
        return { success: false, error: 'Ошибка сервера: неполные данные' };
      }

      if (data.token) {
        this.storeToken(data.token);
      }
      this.storeUser(data.user);
      // Логируем только в dev режиме
      if (import.meta.env.DEV) {
        console.log('🔐 User authenticated successfully');
      }

      this.notifyAuthChange();

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Ошибка подключения к серверу' };
    }
  }

  // Выход
  async logout(): Promise<void> {
    try {
      // Вызываем API для удаления cookie на сервере
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {});
      
      // Очищаем все данные из localStorage
      this.clearToken();
      this.clearUser();
      this.clearAccessCache();
      console.log('🔐 Logged out, localStorage cleared');
    } catch (e) {
      console.error('🔐 Logout error:', e);
      this.clearToken();
      this.clearUser();
      this.clearAccessCache();
    }
    this.notifyAuthChange();
  }

  // Проверить доступ к сервису
  async checkAccess(): Promise<AccessCheck> {
    const token = this.getStoredToken();
    const user = this.getStoredUser();
    
    if (!user) {
      console.log('🔐 checkAccess: no user');
      return { allowed: false, reason: 'Требуется авторизация' };
    }

    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      console.log('🔐 checkAccess: checking with server...');
      const response = await fetch(`${API_URL}/api/auth/check-access`, {
        headers,
        credentials: 'include'
      });

      if (response.status === 401) {
        // Токен невалидный - выходим
        console.log('🔐 checkAccess: token invalid (401)');
        await this.logout();
        return { allowed: false, reason: 'Сессия истекла' };
      }

      if (!response.ok) {
        console.warn(`🔐 checkAccess: non-auth server error (${response.status}), using grace access`);
        return this.getOfflineGraceAccess(user);
      }

      const result = await response.json();
      console.log('🔐 checkAccess result:', result);
      this.storeAccess(result);
      return result;
    } catch (error) {
      console.error('🔐 Check access error:', error);
      if (user) {
        console.log('🔐 Network error but have user, using grace access');
        return this.getOfflineGraceAccess(user);
      }
      return { allowed: false, reason: 'Ошибка подключения к серверу' };
    }
  }

  // Обновить информацию о пользователе
  async refreshUser(): Promise<void> {
    const token = this.getStoredToken();
    const user = this.getStoredUser();
    if (!token && !user) return;

    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.storeUser(data.user);
        this.notifyAuthChange();
      } else if (response.status === 401) {
        await this.logout();
      } else {
        console.warn(`Refresh user skipped due to server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  }

  /**
   * Проверяет валидность текущего токена через API
   * Возвращает true если токен валиден, false если нет
   */
  async validateToken(): Promise<boolean> {
    const token = this.getStoredToken();
    const user = this.getStoredUser();
    if (!token && !user) return false;

    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers,
        credentials: 'include'
      });

      if (response.status === 401) {
        // Токен реально невалиден
        console.warn('🔐 Токен невалиден при валидации');
        await this.logout();
        return false;
      }

      if (!response.ok) {
        console.warn(`🔐 validateToken: server error ${response.status}, keeping session`);
        return true;
      }

      // Обновляем данные пользователя
      const data = await response.json();
      this.storeUser(data.user);
      this.notifyAuthChange();
      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      // При ошибке сети считаем токен валидным (offline mode)
      return true;
    }
  }
  
  // Получить заголовки для API запросов
  getAuthHeaders(): Record<string, string> {
    const token = this.getStoredToken();
    if (!token) return {};
    return { 'Authorization': `Bearer ${token}` };
  }
}

// Singleton экземпляр
export const authService = new AuthService();
export default authService;

